import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Calendar,
  BarChart3,
  ListChecks,
  User,
  CreditCard,
  LayoutDashboard
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroupLabel,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { AppVersionTag } from "@/components/common/AppVersionTag";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";
import { useTouchNavigation } from "@/hooks/useTouchNavigation";
import { useUserProfile } from "@/hooks/useUserProfile";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export function AthleteSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { makePointerDownHandler, onPointerMove, makePointerUpNavigateHandler, onPointerCancel, makeKeyHandler } = useTouchNavigation();

  const isMobile = useIsMobile();
  const { setOpenMobile } = useSidebar();
  const { t } = useTranslation("athlete");
  const { logoUrl, brandName } = useThemeCustomization();
  const { profile } = useUserProfile();

  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [location.pathname, isMobile, setOpenMobile]);

  const menuItems = [
    { label: t("tabs.dashboard"), icon: LayoutDashboard, path: "/athlete/dashboard" },
    { label: t("tabs.schedule"), icon: Calendar, path: "/athlete/schedule" },
    { label: t("tabs.reservations"), icon: ListChecks, path: "/athlete/reservations" },
    { label: t("tabs.stats"), icon: BarChart3, path: "/athlete/stats" },
    { label: t("tabs.subscriptions"), icon: CreditCard, path: "/athlete/subscriptions" },
    { label: t("tabs.userProfile"), icon: User, path: "/athlete/profile" },
  ];

  const initials = profile 
    ? `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase() || 'A'
    : 'A';

  return (
    <Sidebar className="sidebar border-r border-sidebar-border lg:pt-16 bg-sidebar backdrop-blur-sm">
      <SidebarHeader className="py-6">
        <div className="flex flex-col items-center gap-4">
          <img 
            src={logoUrl}
            alt={`${brandName} Logo`}
            className="h-14 w-auto object-contain" 
          />
          
          {/* User avatar */}
          <div className="flex items-center gap-3 w-full px-4 py-2 rounded-lg bg-sidebar-accent">
            <Avatar className="h-9 w-9 border-2 border-primary/30">
              <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">
                {profile?.first_name || t("tabs.dashboard")}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {t("tabs.dashboard")}
              </p>
            </div>
          </div>
        </div>
      </SidebarHeader>
        
      <SidebarContent>
        <SidebarMenu>
          <SidebarGroupLabel>{t("menu", { ns: "common" })}</SidebarGroupLabel>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton
                  type="button"
                  draggable={false}
                  data-nav-path={item.path}
                  onPointerDown={makePointerDownHandler(item.path)}
                  onPointerMove={onPointerMove}
                  onPointerUp={makePointerUpNavigateHandler(item.path)}
                  onPointerCancel={onPointerCancel}
                  onKeyDown={makeKeyHandler(item.path)}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                  }}
                  className={cn(
                    "group flex items-center gap-3 transition-all min-h-11 px-3 rounded-lg mx-1",
                    "touch-manipulation select-none",
                    isActive 
                      ? "bg-primary/10 text-primary font-medium border-l-2 border-primary" 
                      : "hover:bg-muted/50"
                  )}
                  role="link"
                  aria-current={isActive ? "page" : undefined}
                  aria-label={item.label}
                >
                  <item.icon
                    className={cn(
                      "h-5 w-5 transition-colors",
                      isActive
                        ? "text-primary"
                        : "text-muted-foreground group-hover:text-foreground"
                    )}
                  />
                  <span>{item.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarContent>
        
      <SidebarFooter className="border-t py-2 flex items-center justify-center">
        <AppVersionTag />
      </SidebarFooter>
    </Sidebar>
  );
}
